import { env } from '../config/env';
import { applyApiAuthHeaders } from '../api/createClient';
import { ApiError } from '../api/errors';
import type { ApiAudience } from './audiences';

export interface AuthUser {
  user_id: number;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

function joinUrl(path: string): string {
  const base = env.authApiUrl.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

function buildHref(path: string): string {
  const url = new URL(joinUrl(path), window.location.origin);
  return env.authApiUrl.startsWith('http') ? url.href : `${url.pathname}${url.search}`;
}

async function ensureAuthToken(): Promise<boolean> {
  const { authService } = await import('./index');
  return authService.ensureAccessToken('auth' as ApiAudience);
}

export async function fetchAuthUsersPage(offset = 0, limit = 500): Promise<AuthUser[]> {
  const href = buildHref(`/users?offset=${offset}&limit=${limit}`);
  const ok = await ensureAuthToken();
  if (!ok) {
    console.warn('[authUsersApi] could not obtain auth token (app botto-auth)');
    throw new ApiError('Cannot obtain auth-service access token', 401, null);
  }
  const headers = new Headers();
  applyApiAuthHeaders(headers, 'auth' as ApiAudience);
  const res = await fetch(href, { method: 'GET', headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      const ct = res.headers.get('content-type') || '';
      body = ct.includes('application/json') ? await res.json() : await res.text();
    } catch {
      /* ignore */
    }
    console.warn('[authUsersApi] GET /users failed:', res.status, body);
    throw new ApiError(`HTTP ${res.status} ${res.statusText}`, res.status, body);
  }
  return (await res.json()) as AuthUser[];
}

/** Loads all auth users (paginated, up to 500 per page). */
export async function fetchAllAuthUsers(): Promise<AuthUser[]> {
  const pageSize = 500;
  const all: AuthUser[] = [];
  let offset = 0;

  while (true) {
    const page = await fetchAuthUsersPage(offset, pageSize);
    all.push(...page);
    if (page.length < pageSize) break;
    offset += pageSize;
  }

  return all;
}
